import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchDefaultUpiId } from '../../../services/adminSettingsService';
import UpiSetupDialog from '../../../components/billing/UpiSetupDialog';
import {
  QR_INITIAL_DURATION,
  openQrPopupWindow,
  renderQrPopupContent,
} from '../../../utils/qrPaymentPopup';
import '../../Bills/SmartBillWithQR.css';

const PAYMENT_METHODS = [
  { key: 'cash', icon: 'fas fa-money-bill-wave', color: '#28a745', title: 'Cash', subtitle: 'Physical Payment' },
  { key: 'upi', icon: 'fab fa-google-pay', color: '#4285f4', title: 'UPI', subtitle: 'Google Pay, PhonePe' },
  { key: 'card', icon: 'fas fa-credit-card', color: '#6c757d', title: 'Card', subtitle: 'Credit/Debit Card' },
  { key: 'aeps', icon: 'fas fa-fingerprint', color: '#0ea5e9', title: 'AEPS', subtitle: 'Aadhaar Enabled' },
];

const createEmptyPayment = () => ({
  methods: { cash: 0, upi: 0, card: 0, aeps: 0, credit: 0 },
  totalPaid: 0,
  change: 0,
});

const createEmptyTouched = () => ({
  cash: false,
  upi: false,
  card: false,
  aeps: false,
  credit: false,
});

const normalizeInitialPayment = (initial) => {
  if (!initial || typeof initial !== 'object') return createEmptyPayment();
  const methods = { ...createEmptyPayment().methods, ...(initial.methods || {}) };
  return {
    methods,
    totalPaid: Number(initial.totalPaid) || 0,
    change: Number(initial.change) || 0,
  };
};

const StudentBillPaymentSection = ({
  totalBill,
  customerName,
  customerPhone,
  courseLabels = [],
  onPaymentChange,
  initialPaymentData = null,
  disabled = false,
}) => {
  const [paymentData, setPaymentData] = useState(() => normalizeInitialPayment(initialPaymentData));
  const [paymentInputTouched, setPaymentInputTouched] = useState(createEmptyTouched);
  const [paymentRequestSent, setPaymentRequestSent] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [pendingPaymentAmount, setPendingPaymentAmount] = useState(0);
  const [adminUpiId, setAdminUpiId] = useState(null);
  const [upiSetupDialogOpen, setUpiSetupDialogOpen] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrModalData, setQrModalData] = useState(null);
  const [qrTimer, setQrTimer] = useState(QR_INITIAL_DURATION);
  const [qrRegenerated, setQrRegenerated] = useState(false);
  const paymentMethodInputRefs = useRef({});
  const qrPopupWindowRef = useRef(null);

  useEffect(() => {
    fetchDefaultUpiId()
      .then((upiId) => setAdminUpiId(upiId))
      .catch((err) => console.warn('UPI config load failed:', err));
  }, []);

  const pushPayment = (next) => {
    setPaymentData(next);
    onPaymentChange?.(next);
  };

  const updatePaymentMethod = (method, amount) => {
    const parsedAmount = parseFloat(amount);
    const sanitizedAmount = Number.isNaN(parsedAmount) ? 0 : parsedAmount;
    setPaymentInputTouched((prev) => ({ ...prev, [method]: true }));
    const newMethods = { ...paymentData.methods, [method]: sanitizedAmount };
    const paidMethods = { ...newMethods };
    delete paidMethods.credit;
    const totalPaid =
      Math.round(Object.values(paidMethods).reduce((sum, val) => sum + (Number(val) || 0), 0) * 100) / 100;
    const change = Math.max(0, Math.round((totalPaid - totalBill) * 100) / 100);
    pushPayment({
      methods: { ...newMethods, credit: Number(newMethods.credit) || 0 },
      totalPaid,
      change,
    });
  };

  useEffect(() => {
    if (!initialPaymentData) return;
    const normalized = normalizeInitialPayment(initialPaymentData);
    setPaymentData(normalized);
    const touched = {};
    Object.keys(normalized.methods).forEach((key) => {
      if (Number(normalized.methods[key]) > 0) touched[key] = true;
    });
    setPaymentInputTouched((prev) => ({ ...prev, ...touched }));
    onPaymentChange?.(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPaymentData]);

  const dueAmount = useMemo(
    () => Math.max(0, Math.round((totalBill - paymentData.totalPaid) * 100) / 100),
    [totalBill, paymentData.totalPaid],
  );

  const closeQrPopup = () => {
    setShowQRModal(false);
    if (qrPopupWindowRef.current && !qrPopupWindowRef.current.closed) {
      qrPopupWindowRef.current.close();
    }
    qrPopupWindowRef.current = null;
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__locsyCloseQrPopup = closeQrPopup;
    }
    return () => {
      if (typeof window !== 'undefined' && window.__locsyCloseQrPopup === closeQrPopup) {
        delete window.__locsyCloseQrPopup;
      }
    };
  }, []);

  useEffect(() => {
    if (!showQRModal || qrTimer <= 0) return undefined;

    const interval = setInterval(() => {
      setQrTimer((prev) => {
        if (prev <= 1) {
          if (!qrRegenerated && qrModalData?.upiId) {
            setTimeout(() => {
              const timestamp = Date.now();
              const newUpiString = `upi://pay?pa=${qrModalData.upiId}&pn=KNLS&am=${qrModalData.amount}&cu=INR&tn=Payment-${timestamp}`;
              const newQrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(newUpiString)}`;
              setQrModalData((prevData) => ({ ...prevData, qrCodeUrl: newQrCodeUrl }));
              setQrRegenerated(true);
              setQrTimer(60);
            }, 2000);
            return 0;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showQRModal, qrTimer, qrRegenerated, qrModalData]);

  useEffect(() => {
    if (!showQRModal || !qrModalData) return;
    const popupExists = renderQrPopupContent(qrPopupWindowRef, qrModalData, qrTimer, qrRegenerated);
    if (!popupExists) {
      setShowQRModal(false);
    }
  }, [showQRModal, qrModalData, qrTimer, qrRegenerated]);

  useEffect(() => {
    if (!showQRModal && qrPopupWindowRef.current && !qrPopupWindowRef.current.closed) {
      qrPopupWindowRef.current.close();
      qrPopupWindowRef.current = null;
    }
  }, [showQRModal]);

  const generatePaymentRequest = () => {
    if (!customerName?.trim()) {
      window.alert('⚠️ Please enter student name first.');
      return;
    }
    const phone = String(customerPhone || '').replace(/\D/g, '');
    if (phone.length !== 10) {
      window.alert('⚠️ Please enter a valid 10-digit phone number.');
      return;
    }
    if (totalBill <= 0) {
      window.alert('⚠️ Please select at least one course with a fee.');
      return;
    }

    const pendingBalance = Math.max(0, Math.round((totalBill - paymentData.totalPaid) * 100) / 100);
    const manualUpiAmount = Number(paymentData.methods?.upi || 0);
    const amountToCollect = manualUpiAmount > 0 ? manualUpiAmount : pendingBalance;

    if (amountToCollect <= 0) {
      window.alert('⚠️ No pending amount! The bill is already fully paid.');
      return;
    }

    if (!adminUpiId) {
      setUpiSetupDialogOpen(true);
      return;
    }

    const amount = Number.isInteger(amountToCollect)
      ? amountToCollect.toFixed(0)
      : amountToCollect.toFixed(2);

    const timestamp = Date.now();
    const upiString = `upi://pay?pa=${adminUpiId}&pn=KNLS&am=${amount}&cu=INR&tn=StudentFee-${timestamp}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(upiString)}`;

    const servicesList =
      courseLabels.length > 0
        ? courseLabels.map((name, index) => ({ index: index + 1, name }))
        : [{ index: 1, name: 'Student course fees' }];

    const popup = openQrPopupWindow(qrPopupWindowRef, closeQrPopup);
    if (!popup) {
      toast.error('Please allow pop-ups to show the QR payment window.');
      return;
    }

    setPendingPaymentAmount(Number(amountToCollect));
    setQrModalData({
      name: customerName,
      phone,
      amount,
      upiId: adminUpiId,
      servicesList,
      qrCodeUrl,
      timestamp: new Date().toLocaleString('en-IN'),
    });
    setQrTimer(QR_INITIAL_DURATION);
    setQrRegenerated(false);
    setShowQRModal(true);
    setPaymentRequestSent(true);
    setPaymentVerified(false);

    window.alert(
      '✅ Payment QR generated!\n\nShow the QR to the student to scan and pay.\n\nAfter payment is received, click "Confirm Payment Received".',
    );
  };

  const confirmPaymentReceived = () => {
    const currentPaid = Number(paymentData.totalPaid || 0);
    const balanceDue = Math.max(0, Math.round((totalBill - currentPaid) * 100) / 100);
    const requestedAmount = Math.max(0, Number(pendingPaymentAmount) || 0);
    const amountToConfirm = requestedAmount > 0 ? requestedAmount : balanceDue;

    if (amountToConfirm <= 0) {
      window.alert('⚠️ No pending payment to confirm. Generate a payment request first.');
      return;
    }

    const confirmed = window.confirm(
      `Did you receive payment of ₹${amountToConfirm.toFixed(2)}?\n\n✅ OK if payment received\n❌ Cancel to wait`,
    );
    if (!confirmed) return;

    const shouldApplyAmount = balanceDue > 0;
    const amountToApply = shouldApplyAmount ? amountToConfirm : 0;
    const newUpiTotal = shouldApplyAmount
      ? Math.round((Number(paymentData.methods?.upi || 0) + amountToApply) * 100) / 100
      : Number(paymentData.methods?.upi || 0);
    const newTotalPaid = shouldApplyAmount
      ? Math.min(totalBill, Math.round((currentPaid + amountToApply) * 100) / 100)
      : currentPaid;
    const changeAmount = Math.max(0, Math.round((currentPaid + amountToApply - totalBill) * 100) / 100);

    pushPayment({
      ...paymentData,
      methods: {
        ...paymentData.methods,
        upi: newUpiTotal,
      },
      totalPaid: newTotalPaid,
      change: changeAmount,
    });
    setPaymentInputTouched((prev) => ({ ...prev, upi: true }));
    setPaymentVerified(true);
    setPendingPaymentAmount(0);
    closeQrPopup();
    toast.success(`Payment ₹${amountToApply.toFixed(2)} added to UPI. You can now submit registration.`);
  };

  return (
    <>
      <div className="smart-bill-qr-row one" style={{ marginTop: 24 }}>
        <div className="smart-bill-qr-payment-section">
          <div className="smart-bill-qr-payment-header">
            <h3>
              <i className="fas fa-credit-card" /> Payment Information
            </h3>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px' }}>
            <div>
              <h4 style={{ marginBottom: '15px' }}>Payment Methods</h4>
              <div style={{ display: 'grid', gap: '15px' }}>
                {PAYMENT_METHODS.map((method) => (
                  <div
                    key={method.key}
                    className="smart-bill-qr-payment-option"
                    style={{ display: 'flex', alignItems: 'center', gap: '15px' }}
                  >
                    <i className={method.icon} style={{ fontSize: '24px', color: method.color }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold' }}>{method.title}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{method.subtitle}</div>
                    </div>
                    <div style={{ width: '120px' }}>
                      <input
                        type="number"
                        className="smart-bill-qr-form-control"
                        placeholder="₹0"
                        disabled={disabled}
                        ref={(el) => {
                          paymentMethodInputRefs.current[method.key] = el;
                        }}
                        value={
                          paymentData.methods[method.key] === 0
                            ? paymentInputTouched[method.key]
                              ? '0'
                              : ''
                            : paymentData.methods[method.key]
                        }
                        onChange={(e) => updatePaymentMethod(method.key, e.target.value)}
                        style={{ textAlign: 'center' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 style={{ marginBottom: '15px' }}>Payment Summary</h4>
              <div
                style={{
                  background: '#e8f5e8',
                  padding: '15px',
                  borderRadius: '8px',
                  marginBottom: '15px',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#28a745' }}>Bill Summary:</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span>Selected Courses:</span>
                  <span>₹{Number(totalBill || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span>Coupon Benefits:</span>
                  <span style={{ color: '#28a745', fontWeight: 'bold' }}>-₹0.00</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                  <span>Coupon Bill Total:</span>
                  <span>₹0.00</span>
                </div>
                <hr style={{ margin: '10px 0' }} />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: '#28a745',
                  }}
                >
                  <span>Total Bill Amount:</span>
                  <span>₹{Number(totalBill || 0).toFixed(2)}</span>
                </div>
              </div>

              <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>Payment Breakdown:</div>
                {PAYMENT_METHODS.map(
                  (method) =>
                    paymentData.methods[method.key] > 0 && (
                      <div
                        key={method.key}
                        style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}
                      >
                        <span>
                          <i className={method.icon} style={{ color: method.color, marginRight: '8px' }} />
                          {method.title}:
                        </span>
                        <span>₹{paymentData.methods[method.key].toFixed(2)}</span>
                      </div>
                    ),
                )}
                <hr style={{ margin: '10px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px' }}>
                  <span>Total Paid:</span>
                  <span>₹{paymentData.totalPaid.toFixed(2)}</span>
                </div>
              </div>

              <div className="smart-bill-qr-change-section">
                <div className="smart-bill-qr-change-row">
                  <span>Total Bill:</span>
                  <span>₹{Number(totalBill || 0).toFixed(2)}</span>
                </div>
                <div className="smart-bill-qr-change-row">
                  <span>Total Paid:</span>
                  <span>₹{paymentData.totalPaid.toFixed(2)}</span>
                </div>
                {paymentData.totalPaid - totalBill > 0 ? (
                  <div className="smart-bill-qr-change-row change">
                    <span>
                      <i className="fas fa-arrow-up" /> Change to Return:
                    </span>
                    <span>₹{(paymentData.totalPaid - totalBill).toFixed(2)}</span>
                  </div>
                ) : paymentData.totalPaid - totalBill < 0 ? (
                  <div className="smart-bill-qr-change-row due">
                    <span>
                      <i className="fas fa-arrow-down" /> Due Amount:
                    </span>
                    <span>₹{dueAmount.toFixed(2)}</span>
                  </div>
                ) : (
                  <div className="smart-bill-qr-change-row" style={{ fontWeight: 'bold', color: '#28a745' }}>
                    <span>
                      <i className="fas fa-check" /> Balance:
                    </span>
                    <span>₹0.00</span>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '15px' }}>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={disabled}
                  onClick={() => {
                    setPaymentInputTouched((prev) => ({ ...prev, cash: true }));
                    pushPayment({
                      methods: { cash: totalBill, upi: 0, card: 0, aeps: 0, credit: 0 },
                      totalPaid: totalBill,
                      change: 0,
                    });
                  }}
                  style={{ marginRight: '10px' }}
                >
                  Full Cash
                </button>
                <button
                  type="button"
                  className="btn btn-info"
                  disabled={disabled}
                  onClick={() => {
                    setPaymentInputTouched((prev) => ({ ...prev, cash: true }));
                    const roundedAmount = Math.ceil(totalBill);
                    pushPayment({
                      methods: { cash: roundedAmount, upi: 0, card: 0, aeps: 0, credit: 0 },
                      totalPaid: roundedAmount,
                      change: roundedAmount - totalBill,
                    });
                  }}
                >
                  Round Up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="smart-bill-qr-row one" style={{ marginTop: 16 }}>
        <div className="smart-bill-qr-footer-actions">
          {totalBill > paymentData.totalPaid && !paymentRequestSent && (
            <>
              <button
                type="button"
                className="btn btn-warning"
                disabled={disabled}
                onClick={generatePaymentRequest}
                style={{ fontSize: '22px', padding: '18px 40px' }}
              >
                <i className="fas fa-qrcode" style={{ fontSize: '28px' }} />
                Generate Payment Request
                <i className="fas fa-mobile-alt" style={{ fontSize: '28px' }} />
              </button>
              <div style={{ color: 'white', marginTop: '10px', fontSize: '14px' }}>
                Opens QR code popup for student to scan and pay (₹{dueAmount.toFixed(2)} due)
              </div>
            </>
          )}

          {paymentRequestSent && !paymentVerified && (
            <>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={disabled}
                  onClick={confirmPaymentReceived}
                  style={{ fontSize: '22px', padding: '18px 40px' }}
                >
                  <i className="fas fa-check-circle" style={{ fontSize: '28px' }} />
                  Confirm Payment Received
                  <i className="fas fa-rupee-sign" style={{ fontSize: '28px' }} />
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  disabled={disabled}
                  onClick={generatePaymentRequest}
                  style={{ fontSize: '22px', padding: '18px 40px' }}
                >
                  <i className="fas fa-qrcode" style={{ fontSize: '28px' }} />
                  Regenerate QR Code
                  <i className="fas fa-redo" style={{ fontSize: '28px' }} />
                </button>
              </div>
              <div style={{ color: '#FFD700', marginTop: '10px', fontSize: '15px', fontWeight: 'bold' }}>
                ⏳ Waiting for payment — show QR to student, then confirm when paid
              </div>
            </>
          )}

          {paymentVerified && totalBill > paymentData.totalPaid && (
            <div style={{ color: '#90EE90', fontSize: '15px', fontWeight: 'bold' }}>
              ✓ Payment recorded in UPI field. Complete registration below or add more payment.
            </div>
          )}
        </div>
      </div>

      <UpiSetupDialog
        open={upiSetupDialogOpen}
        onClose={() => setUpiSetupDialogOpen(false)}
        onSaved={(upiId) => {
          setAdminUpiId(upiId);
          setUpiSetupDialogOpen(false);
        }}
      />
    </>
  );
};

export default StudentBillPaymentSection;
export { createEmptyPayment };
